import { API_BASE_URL } from "./advancedSettings";

export type EmailChangeResult =
  | { status: "sent" }
  | { status: "in-use" }
  | { status: "invalid"; message: string }
  | { status: "reauthenticate" }
  | { status: "wait"; retryAfterSeconds: number }
  | { status: "error" };

/**
 * Asks the API to start a sign-in email change: it emails a confirmation link to the new address and a notice to the current
 * one. The login token must be fresh (the caller re-checks the password and then refreshes it). The email only changes once
 * the link is opened. Never throws: the caller shows the matching message.
 */
export async function requestEmailChange(getToken: () => Promise<string>, newEmail: string): Promise<EmailChangeResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/email/RequestChange`, {
      method: "POST",
      headers: { Authorization: `Bearer ${await getToken()}`, "Content-Type": "application/json" },
      body: JSON.stringify({ newEmail }),
    });
    if (response.ok) return { status: "sent" };
    const body = await response.json().catch(() => ({}));
    if (response.status === 409) return { status: "in-use" };
    if (response.status === 403) return { status: "reauthenticate" };
    if (response.status === 429) return { status: "wait", retryAfterSeconds: Number(body.retryAfterSeconds) || 60 };
    if (response.status === 400) return { status: "invalid", message: String(body.message || "Please enter a valid email address.") };
    return { status: "error" };
  } catch (error) {
    console.error("Could not request the email change:", error);
    return { status: "error" };
  }
}
